import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import type { StoredEntityUpload } from './upload-types.js'
import { getEntityTypeFromMime } from '@tunasong/models'

/**
 * Strip a trailing audio file extension (case-insensitive) from a filename so
 * the user-visible entity title doesn't carry the filesystem extension. Covers
 * every audio extension the manifest accepts (`apps/tunasong/manifest`); keep
 * the two lists in sync.
 *
 * @see https://www.notion.so/3538e623975c81af9e59dd6c11736e53
 */
const audioExtensionPattern = /\.(mp3|wav|ogg|m4a|aac|mp4|webm|flac)$/i
export const stripAudioExtension = (name: string): string => name.replace(audioExtensionPattern, '')

/**
 * The MIME type to upload a file with. Browsers leave `File.type` empty when they do not recognise the
 * extension, and files set on an input programmatically (e.g. via a DataTransfer) can arrive without one,
 * so fall back to the type for the extension.
 */
export const getUploadMimeType = async (
  file: Pick<File, 'name' | 'type'>,
  lookupMimeType: (name: string) => Promise<string | undefined>
) => file.type || lookupMimeType(file.name)

export const blobUploadToTemporaryEntity = ({
  blobUpload,
  userId,
}: {
  blobUpload: StoredEntityUpload
  userId: string
}): Persisted<Entity> => {
  const type = (blobUpload.type as EntityType) ?? getEntityTypeFromMime(blobUpload.blob.type)
  if (!type) {
    throw new Error(`Cannot upload a file with mime type ${blobUpload.blob.type}`)
  }
  return {
    id: blobUpload.id,
    parentId: blobUpload.parentId ?? undefined,
    name: blobUpload.name,
    type,
    storageUrls: {
      // @todo this expires, but we are not using it...
      expiresAtISODateTime: new Date().toISOString(),
      url: URL.createObjectURL(blobUpload.blob),
    },
    userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}
